// Card #1798 — a single, pure classifier for "would the public board render
// real content for this mode/fields, or fall back to Blank". Two call sites
// share this rather than re-deriving the same `layoutFamilyForModeType`/
// `fields` presence check independently: ScoreboardContent.jsx (decides
// what to actually render) and ScoreboardPage.jsx (AC8 — derives an
// operator-facing status line from the SAME rule, via `onRenderStatus` +
// `postScoreboardStatus` over `boardStatusChannel`, without needing
// score-api to change at all). Keeping this in one place means the two
// never drift on what counts as "nothing to show."
//
// Deliberately pure and defensive: never throws on a missing/malformed
// `mode` (mirrors layoutFamilyForModeType's own fails-closed contract).
import { FIELD_SETS, resolveFieldSet } from "score-common/constants";
import { cleanRiderStatus } from "score-common/rider-status";
import { layoutFamilyForModeType, LAYOUT_FAMILY } from "./layoutFamilies";

export const BOARD_RENDER_REASON = {
  // Real content, or the Blank mode type configured on purpose — either way
  // the board is doing exactly what it was told to.
  OK: "ok",
  // `mode.modeType` doesn't resolve to any of the known layout families:
  // unset, unrecognized, or removed (#1798 AC5).
  UNKNOWN_MODE: "unknown-mode",
  // A recognized mode type, but score-api composed no `fields` for it right
  // now (no active class/ride/context resolved for this view, #1798 AC1 /
  // #1786's case).
  NO_ACTIVE_CLASS: "no-active-class",
  // MESSAGE resolved but its configured text is missing/whitespace-only
  // (#1795 AC6 / hard-requirement-3's "empty message routes to Blank").
  MESSAGE_EMPTY: "message-empty",
  // Ranked-list family (End of Class Result) resolved, but the class has
  // zero ranked rows (#2439): nothing scored yet, a wrong class pinned, or
  // every entry scratched. Falls back to Blank rather than a bare banner +
  // class header over an empty table.
  RANKED_LIST_EMPTY: "ranked-list-empty",
  // Card #2485 -- an IMAGE mode with no resolvable imageAssetUrl (unset,
  // deleted or foreign-org asset): the universal Blank/logo fallback.
  IMAGE_EMPTY: "image-empty",
  // Card #2483 -- a MID_RIDE_SIMPLE mode whose `fields` is present but has
  // nothing the layout can draw (e.g. `{}`): Blank, never an empty frame.
  MID_RIDE_EMPTY: "mid-ride-empty",
  // Card #3098 -- a CLASS_SUMMARY mode whose `fields` is present but has
  // nothing the layout can draw (e.g. `{}`): Blank, never an empty frame.
  CLASS_SUMMARY_EMPTY: "class-summary-empty",
};

// Card #2483 -- true when a MID_RIDE_SIMPLE `fields` carries at least one thing
// MidRideSimpleLayout can draw (a class header or any identity part). Mirrors
// the layout's own per-field gates, so the blank decision and the render
// cannot disagree. `{}` and `{ entryIdentity: {} }` are truthy but empty.
export function hasMidRideSimpleContent(fields) {
  if (!fields) return false;
  const nn = fields.classNumberName;
  const hasClassHeader = (!!nn && (nn.classNumber != null || !!nn.className)) || !!fields.testName;
  const id = fields.entryIdentity || {};
  const hasIdentity = id.competitorNumber != null || !!id.riderName || !!cleanRiderStatus(id.riderStatus) || !!id.horseName;
  return hasClassHeader || hasIdentity;
}

// Card #3098 -- true when a CLASS_SUMMARY `fields` carries at least one thing
// ClassSummaryLayout can draw, for either field set: a class header, a judge,
// a time, a ride count, a ring, or a start list row. `{}` is truthy but empty.
export function hasClassSummaryContent(fields) {
  if (!fields) return false;
  const nn = fields.classNumberName;
  const hasClassHeader = (!!nn && (nn.classNumber != null || !!nn.className)) || !!fields.testName;
  return (
    hasClassHeader ||
    (Array.isArray(fields.judgesPositions) && fields.judgesPositions.length > 0) ||
    (Array.isArray(fields.startList) && fields.startList.length > 0) ||
    !!fields.classStart ||
    !!fields.estimatedFinish ||
    fields.totalRides != null ||
    !!fields.ring
  );
}

// Returns { blank, reason }. `blank` is true whenever ScoreboardContent
// falls back to BlankLayout instead of a real layout family.
export function classifyBoardRenderState({ mode, fields } = {}) {
  const modeType = mode?.modeType;
  const family = layoutFamilyForModeType(modeType);

  if (family === LAYOUT_FAMILY.BLANK) {
    return { blank: true, reason: BOARD_RENDER_REASON.OK };
  }
  // MESSAGE never flows its text through score-api's `fields` (it lives on
  // mode.message, resolved client-side like BLANK's logoAsset), so the
  // generic `!fields` check below is meaningless for it — `fields` is
  // always undefined here regardless of whether the message is set, and a
  // composed `{}` would be truthy anyway. Handled explicitly before that
  // check runs, mirroring the BLANK branch above.
  if (family === LAYOUT_FAMILY.MESSAGE) {
    const hasMessage =
      typeof mode?.message === "string" && mode.message.trim() !== "";
    return hasMessage
      ? { blank: false, reason: BOARD_RENDER_REASON.OK }
      : { blank: true, reason: BOARD_RENDER_REASON.MESSAGE_EMPTY };
  }
  if (family === LAYOUT_FAMILY.IMAGE) {
    const hasImage = typeof mode?.imageAssetUrl === "string" && mode.imageAssetUrl.trim() !== "";
    return hasImage
      ? { blank: false, reason: BOARD_RENDER_REASON.OK }
      : { blank: true, reason: BOARD_RENDER_REASON.IMAGE_EMPTY };
  }
  if (!family) {
    return { blank: true, reason: BOARD_RENDER_REASON.UNKNOWN_MODE };
  }
  if (!fields) {
    return { blank: true, reason: BOARD_RENDER_REASON.NO_ACTIVE_CLASS };
  }
  // Card #2439 -- decision (recorded on the card): zero ranked rows routes to
  // Blank + logo, the same answer MESSAGE_EMPTY gives for an empty message
  // and the standing "the board never shows something misleading" rule. This
  // applies ONLY to a truly empty list (nothing confirmed/final yet, or a
  // wrong class pinned). An all-eliminated / all-no-show class that has
  // results is NOT empty: score-api's composeRankedList
  // (apps/score-api/src/lib/scoreboard-mode-payload.js) keeps confirmed/final
  // rides carrying a term reason as outcome rows, so it still renders the
  // banner + outcome rows. A missing or non-array rankedList is treated as
  // empty (fails closed).
  if (family === LAYOUT_FAMILY.RANKED_LIST) {
    const hasRows = Array.isArray(fields.rankedList) && fields.rankedList.length > 0;
    if (!hasRows) {
      return { blank: true, reason: BOARD_RENDER_REASON.RANKED_LIST_EMPTY };
    }
  }
  if (family === LAYOUT_FAMILY.MID_RIDE_SIMPLE && !hasMidRideSimpleContent(fields)) {
    return { blank: true, reason: BOARD_RENDER_REASON.MID_RIDE_EMPTY };
  }
  if (family === LAYOUT_FAMILY.CLASS_SUMMARY && !hasClassSummaryContent(fields)) {
    return { blank: true, reason: BOARD_RENDER_REASON.CLASS_SUMMARY_EMPTY };
  }
  return { blank: false, reason: BOARD_RENDER_REASON.OK };
}

// Card #2500 -- does the board for this mode/fields render a layout family
// that was tuned for every supported canvas shape (so ScoreboardFrame should
// let it FILL a declared 4:3 canvas rather than letterbox the 16:9 design
// inside it)? The ranked-list family (End of Class Result and Mid-Class
// Result; RankedListLayout.css carries its @container tuning) and, since card
// #3110, Class Summary DETAILED (ClassSummaryLayout.css). Class Summary SIMPLE
// (the facts grid) is a different field set of the same mode type and is NOT
// tuned, so it keeps the letterbox: the answer follows the field set actually
// rendered (resolveFieldSet, so an absent/unknown value reads as detailed,
// exactly as ClassSummaryLayout does).
//
// Card #2502: Message and Blank fill too, and so does EVERY Blank, the
// fallback ones included (a ranked list with no rows yet, an unknown mode, no
// active class, an empty message, an empty image, no data at all while the
// board loads). Blank is the one layout the whole board falls through to, so
// its answer must not depend on WHY the board is blank: if a fallback Blank
// letterboxed while a real family filled, the footer, logos and clock would
// resize every time a poll flipped a board between data and nothing. A caught
// render error inside a filled board paints ScoreboardErrorBoundary's
// canvas-variant fallback, which is sized to match (a min() max-width, see that
// file). Only Class Summary SIMPLE and the Image family are still untuned and
// keep the letterbox. Pure and defensive, like classifyBoardRenderState.
export function boardFillsCanvas({ mode, fields, slideshow = null } = {}) {
  if (classifyBoardRenderState({ mode, fields }).blank) return true;
  // Card #3314 -- an Image Loop slide fills the canvas edge to edge (it has no
  // 4:3 letterbox). The single Image mode keeps the letterbox, as before.
  if (slideshow && layoutFamilyForModeType(mode?.modeType) === LAYOUT_FAMILY.IMAGE) return true;
  const family = layoutFamilyForModeType(mode?.modeType);
  if (family === LAYOUT_FAMILY.RANKED_LIST) return true;
  // Card #2502 -- a non-empty Message (MessageLayout.css). An empty one is
  // blank above, and Blank itself fills (BlankLayout.css).
  if (family === LAYOUT_FAMILY.MESSAGE) return true;
  // Card #2501 -- the single-entry family (Ride Intro Detailed, Post-Ride
  // Score; SingleEntryLayout.css) and Mid-Ride Simple (MidRideSimpleLayout,
  // verified in real Chrome at 4:3).
  if (family === LAYOUT_FAMILY.SINGLE_ENTRY || family === LAYOUT_FAMILY.MID_RIDE_SIMPLE) return true;
  if (family === LAYOUT_FAMILY.CLASS_SUMMARY) return resolveFieldSet(mode?.fieldSet) === FIELD_SETS.DETAILED;
  return false;
}
